import { PingboardConfig } from '@dadohr/shared/domainTypes/integrations/pingboard';
import { CF } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { LinkExternal } from '../../../../../../components/LinkExternal';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  MASK,
  masked
} from '../common';
import { removePingboardConfig, usePingboardConfig } from './service';

const NAME = 'Pingboard';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const SetupDialog = ({
  config,
  open,
  onClose,
  onSuccessfulIntegration
}: IntegrationSetupDialogWithConfigProperties<PingboardConfig>) => {
  const { t } = useUnsafeTranslation();

  const { clientId, clientSecret } = config?.data || {
    clientId: '',
    clientSecret: ''
  };

  const [state, setState] = useState<{
    clientId: string;
    clientSecret: string;
  }>({
    clientId,
    clientSecret: clientSecret ? MASK : ''
  });
  useEffect(() => {
    setState({ clientId, clientSecret: masked(clientSecret) });
  }, [clientId, clientSecret]);

  if (!config) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t('Connect Pingboard and Dado to import your employee data')}
        </Alert>
        <Typography component="p" variant="body1">
          {t('Create a')}{' '}
          <LinkExternal
            color="primary"
            href={'https://app.pingboard.com/service_accounts'}
          >
            {t('service account in Pingboard')}
          </LinkExternal>{' '}
          {t('to provide the information requested below.')}
        </Typography>

        <FormRow>
          <CustomInputLabel label={t('Client ID')} fullWidth>
            <TextField
              variant="outlined"
              value={state.clientId}
              onChange={(ev) =>
                setState((s) => ({ ...s, clientId: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>
        <FormRow>
          <CustomInputLabel
            label={t('org.integrations.common.apiKey')}
            fullWidth
          >
            <TextField
              variant="outlined"
              value={state.clientSecret}
              onChange={(ev) =>
                setState((s) => ({ ...s, clientSecret: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removePingboardConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
            disabled={!clientId && !clientSecret}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              onClick={() =>
                callFirebaseFunction(CF.pingboard.verifyCredentialsAndStore, {
                  orgId: config.data.orgId,
                  clientId: state.clientId,
                  clientSecret: state.clientSecret
                }).then(onSuccessfulIntegration)
              }
              disabled={
                !state.clientId ||
                !state.clientSecret ||
                state.clientId === config.data.clientId ||
                state.clientSecret === config.data.clientSecret
              }
              pending={`${t('Saving')}...`}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

export const PingboardSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = usePingboardConfig(org.id, userId);
  const cfg = config || null;

  return (
    <IntegrationConfigurationButton
      name={NAME}
      functionalities={functionalities}
      logoUrl="/images/integrations-logos/pingboard.svg"
      active={!!cfg?.data.active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          config={cfg}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
